import { Reference, StoreObject, TypePolicy } from '@apollo/client';
import { isEffectivelyOnline } from '@bilderit/offline-link';

type CacheObjectWithItems = {
  __typename: string;
  items: Reference[];
};

export const localQueries: TypePolicy = {
  fields: {
    getDashBoard: {
      keyArgs: () => ['context'],
    },
    getMyDailyReports: {
      keyArgs: (args) => JSON.stringify(args ?? {}),
      read: (existingData, { args, readField, fieldName }) => {
        if (existingData) return existingData;
        if (args?.filter) {
          const { start_date, end_date, machine_id } = args.filter;
          const noFilter = Object.keys(args)
            .filter((key) => key !== 'filter')
            .filter((key) => !!args[key])
            .reduce((result, key) => ({ ...result, [key]: args[key] }), {});
          const allMyReports = readField({
            fieldName,
            variables: noFilter,
          }) as CacheObjectWithItems;
          return {
            ...allMyReports,
            items: allMyReports?.items?.filter(
              (reportRef) =>
                (!start_date ||
                  new Date(readField('date', reportRef) as string) >=
                    new Date(start_date)) &&
                (!end_date ||
                  new Date(readField('date', reportRef) as string) <=
                    new Date(end_date)) &&
                (!machine_id ||
                  readField('id', readField('machine', reportRef)) ===
                    machine_id)
            ),
          };
        }
      },
    },
    getMachines: {
      keyArgs: (args) =>
        JSON.stringify(
          Object.fromEntries(
            Object.entries(args ?? {}).filter(([key]) => key !== 'pagination')
          ) ?? {}
        ),
      merge(existing: any, incoming: any, { args, readField }) {
        const incomingItems = incoming?.items ?? [];
        const existingItems = existing?.items ?? [];
        const offset = args?.pagination?.offset ?? 0;
        const limit = args?.pagination?.limit ?? Infinity;
        // Create a set of incoming item IDs to identify what's being updated
        const incomingIds = new Set(
          incomingItems.map((item: any) => readField('id', item))
        );

        // Filter out items from existing that are being updated (to avoid duplicates)
        const merged =
          existingItems?.filter((item: any) => {
            const id = readField('id', item);
            return !incomingIds.has(id);
          }) ?? [];

        // Insert the incoming elements in the right places, according to args.
        const end = offset + Math.min(limit, incomingItems.length);
        for (let i = offset; i < end; ++i) {
          merged[i] = incomingItems[i - offset];
        }
        // If the incoming array is shorter
        return { ...incoming, items: merged };
      },
      read: (
        existingData: Record<string, any> | null | undefined,
        { fieldName, args, readField, cache, toReference }
      ) => {
        const offset = args?.pagination?.offset ?? 0;
        const limit = args?.pagination?.limit ?? Infinity;
        let items = existingData?.items;
        // Carries the union __typename so a reconstructed (offline) result can
        // still be resolved by the query's inline fragment.
        let typename = existingData?.__typename;

        // If we have no cache entry at all for this query (e.g. first load
        // while offline), we try reconstructing the list from individually
        // cached machines. We must NOT do this when a cache entry already
        // exists with an empty `items` array: that means the server
        // legitimately returned no machines (e.g. a business-unit filter that
        // matches nothing), and falling back here would surface stale,
        // unfiltered cache contents instead of the real empty result
        // (BIL-4558).
        if (!existingData && (args?.filter || args?.context)) {
          const { fuel_participations, group, id, is_archived, query } =
            args.filter || {};
          const context = args.context;
          const noFilter = Object.keys(args)
            .filter((key) => key !== 'filter')
            .reduce((result, key) => ({ ...result, [key]: args[key] }), {});
          let allMachines = readField<CacheObjectWithItems>({
            fieldName,
            variables: noFilter,
          });
          if (!allMachines) {
            // if we dont have a cache entry for getMachines:{}, try to find all the individual machines in the cache
            const serializedState = cache.extract();
            allMachines = {
              __typename: 'AuthCheckGetMachines',
              items: Object.values(serializedState)
                .filter((item): item is StoreObject => !!item)
                .filter((item) => item.__typename === 'Machine')
                .map((item) => toReference(item))
                .filter((item): item is Reference => !!item),
            };
          }
          if (!allMachines) throw new Error('no machines in cache');
          typename = allMachines.__typename;
          items =
            allMachines?.items
              ?.filter(
                (machineRef) =>
                  (!id || readField('id', machineRef) === id) &&
                  (!query ||
                    readField<string>('name', machineRef)
                      ?.toLowerCase()
                      .includes(query.toLowerCase()) ||
                    readField<string>('domain', machineRef)
                      ?.toLowerCase()
                      .includes(query.toLowerCase()) ||
                    readField<string>('serial_number', machineRef)
                      ?.toLowerCase()
                      .includes(query.toLowerCase()) ||
                    readField<string>('display_type', machineRef)
                      ?.toLowerCase()
                      .includes(query.toLowerCase())) &&
                  (!fuel_participations ||
                    fuel_participations.includes(
                      readField('fuel_participation', machineRef)
                    )) &&
                  (!is_archived ||
                    readField('is_archived', machineRef) === is_archived) &&
                  (is_archived === true
                    ? readField('is_archived', machineRef) === true
                    : readField('is_archived', machineRef) === false) &&
                  (!context?.company_id ||
                    readField('id', readField('company', machineRef)) ===
                      context.company_id) &&
                  (!context?.project_id ||
                    readField('id', readField('project', machineRef)) ===
                      context.project_id) &&
                  (!group ||
                    (
                      readField(
                        'group',
                        readField('type', machineRef)
                      ) as string
                    )
                      ?.toLowerCase()
                      .replace(/_/g, ' ') ===
                      group.toLowerCase().replace(/_/g, ' '))
              )
              .slice(offset, offset + limit) ?? [];
        }

        // If we read the field before any data has been written to the
        // cache, this function will return undefined, which correctly
        // indicates that the field is missing.
        const page = items && items.slice(offset, offset + limit);
        if (page && page.length > 0) {
          // Reconstructed lists have no server total. Return null so queries
          // selecting page_info can still read a complete offline result.
          return {
            __typename: typename,
            ...existingData,
            page_info: existingData?.page_info ?? null,
            items: page,
          };
        }
        // A cache entry exists but yields no items for this page. When we are
        // at the start of the list this is a genuine empty result coming from
        // the server, so surface the empty list instead of reporting the field
        // as missing (which would keep showing stale data) (BIL-4558).
        if (existingData && offset === 0) {
          return {
            ...existingData,
            page_info: existingData.page_info ?? null,
            items: [],
          };
        }
        // Otherwise (no cache entry, or paging past the end of an existing
        // array) report the field as missing.
        return undefined;
      },
    },
    getMachineById: {
      keyArgs: ['id'],
      read: (existingData, { args, toReference }) =>
        existingData || {
          item: toReference({ __typename: 'Machine', id: args?.id }),
          __typename: 'AuthCheckGetMachineById',
        },
    },
    getEmployees: {
      keyArgs: (args) => JSON.stringify(args ?? {}),
      read: (existingData, { args, toReference }) => {
        if (existingData) return existingData;
        if (args?.filter?.user_id && !isEffectivelyOnline('2g')) {
          return {
            items: [
              toReference({
                __typename: 'Employee',
                id: args.filter.user_id,
              }),
            ],
            __typename: 'AuthCheckGetEmployees',
          };
        }
      },
    },
    // A single cache entry regardless of args (no `context`, not even keyed
    // by `machine_ids`): the phone preloads availability in chunks of up to
    // 500 machine ids (usePreloadAvailableChecklists in PreloadData.tsx) and
    // later re-reads it for one machine at a time
    // (useAvailableChecklistsForMachine). Both must land in the same cache
    // entry, or the per-machine read misses the cache and — offline, where
    // cache-only is used — never resolves. The merge unions templates and
    // per-machine availability across chunks instead of the default "last
    // write wins".
    getAvailableChecklists: {
      keyArgs: false,
      merge(existing: any, incoming: any, { readField, args }) {
        // The server omits machines left with no assignments, so a machine
        // that was part of this request and came back absent is known to have
        // none: it is recorded with an empty list (replacing any stale entry),
        // which is what tells it apart from a machine nobody has asked about.
        // The machines the request did not ask about stay as they were
        // (chunked preloads, and single-machine reads that share this entry).
        const requestedMachineIds = new Set<number>(args?.machine_ids ?? []);

        const incomingTemplateIds = new Set(
          (incoming?.templates ?? []).map((template: any) =>
            readField('id', template)
          )
        );
        const templates = [
          ...(existing?.templates ?? []).filter(
            (template: any) =>
              !incomingTemplateIds.has(readField('id', template))
          ),
          ...(incoming?.templates ?? []),
        ];

        const machinesByMachineId = new Map<number, any>();
        for (const machine of existing?.machines ?? []) {
          // machine_id: Int! — always present on a stored entry.
          const machineId = readField('machine_id', machine) as number;
          if (requestedMachineIds.has(machineId)) continue;
          machinesByMachineId.set(machineId, machine);
        }
        for (const machineId of requestedMachineIds) {
          machinesByMachineId.set(machineId, {
            __typename: 'MachineChecklistAvailability',
            machine_id: machineId,
            template_ids: [],
          });
        }
        for (const machine of incoming?.machines ?? []) {
          machinesByMachineId.set(
            readField('machine_id', machine) as number,
            machine
          );
        }

        return {
          ...incoming,
          templates,
          machines: [...machinesByMachineId.values()],
        };
      },
    },
    // A checklist submitted without signal sits in "Mis checklists" as a
    // pending row (serial_number 0, see useSubmitChecklist). When the signal
    // is back the list is refetched, and that answer can be older than the
    // queued send: replacing the list would take the pending row away until
    // the next refetch. The merge keeps the pending rows the answer does not
    // have — it works on the cache's data, so it holds after a reload too,
    // when the send is replayed from the queue. What becomes of the row is
    // decided by its send: if it goes through, the response carries the same
    // Checklist id and the row becomes the synced one in place; if the server
    // rejects it, the row stays and the list shows it as "No enviado" (see
    // useFailedChecklistSendIds) until the send is retried; discarding it from
    // the failed-sends notice removes it (see removeChecklistFromCache).
    getChecklists: {
      merge(existing: any, incoming: any, { readField }) {
        if (!existing?.items || !incoming?.items) return incoming;

        const incomingIds = new Set(
          incoming.items.map((item: any) => readField('id', item))
        );
        const stillPending = existing.items.filter(
          (item: any) =>
            readField('serial_number', item) === 0 &&
            !incomingIds.has(readField('id', item))
        );
        if (!stillPending.length) return incoming;

        return { ...incoming, items: [...stillPending, ...incoming.items] };
      },
    },
    maintenanceDashboardMetrics: {
      keyArgs: (args) =>
        JSON.stringify(
          Object.fromEntries(
            Object.entries(args ?? {}).filter(([key]) => key !== 'pagination')
          ) ?? {}
        ),
    },
  },
};
